/**
 * QuizQuestionList — переиспользуемый список вопросов квиза (ядро правки).
 *
 * Извлечено из инлайн-рендера HostEditor.tsx (Фаза 8). Инкапсулирует список карточек
 * QuestionCard + кнопки добавления по типам + всю логику add/move/delete/changeKind/
 * updateQuestion, выдавая наружу одно событие `onChange` с новым массивом вопросов.
 *
 * Используется и HostEditor (режим создания/правки квизов, состояние в localStorage),
 * и ShowcaseEditor админки (состояние в useState, НЕ localStorage). Алгоритмы
 * add/remove/move/withDraftKind копируют прежнюю логику HostEditor 1:1 — поведение
 * идентично, тесты HostEditor.test.tsx остаются зелёными.
 */
import { useTranslation } from 'react-i18next';
import { makeDraftQuestion, withDraftKind, type DraftQuestion } from '../../drafts';
import { KINDS, type Kind } from '../../protocol';
import { QuestionCard } from './QuestionEditor';

export interface QuizQuestionListProps {
  /** Текущий массив вопросов (контролируемое состояние). */
  questions: DraftQuestion[];
  /** Единый колбэк: новый массив после любого изменения (add/move/delete/kind/text/body/image). */
  onChange: (questions: DraftQuestion[]) => void;
  /** Показывать ли превью «как увидит игрок» в каждой карточке. По умолчанию true. */
  showPreview?: boolean;
  /**
   * Необязательный потолок числа вопросов (из GET /api/limits → questionsPerQuiz).
   * При questions.length >= limit кнопки добавления блокируются.
   * Если не задан — блокировки нет (поведение ShowcaseEditor: лимит валидируется сервером).
   */
  questionsLimit?: number;
  /**
   * Лимит длины блока кода вопроса в рунах (GET /api/limits → codeMaxChars,
   * Фаза 16, B4); опц. — без значения счётчик в секции «Блок кода» скрыт и
   * ввод не обрезается (ShowcaseEditor: лимит валидируется сервером).
   */
  codeMaxChars?: number;
  /** Префикс data-testid для корня (по умолчанию 'quiz-question-list'). */
  testIdPrefix?: string;
}

export function QuizQuestionList({
  questions,
  onChange,
  showPreview = true,
  questionsLimit,
  codeMaxChars,
  testIdPrefix,
}: QuizQuestionListProps): JSX.Element {
  const { t } = useTranslation();

  const questionsFull =
    typeof questionsLimit === 'number' && questionsLimit > 0 && questions.length >= questionsLimit;

  function addQuestion(kind: Kind): void {
    if (questionsFull) return;
    onChange([...questions, makeDraftQuestion(kind)]);
  }

  function updateQuestion(index: number, q: DraftQuestion): void {
    onChange(questions.map((x, i) => (i === index ? q : x)));
  }

  function changeQuestionKind(index: number, kind: Kind): void {
    onChange(questions.map((x, i) => (i === index ? withDraftKind(x, kind) : x)));
  }

  function removeQuestion(index: number): void {
    onChange(questions.filter((_, i) => i !== index));
  }

  function moveQuestion(index: number, dir: -1 | 1): void {
    const target = index + dir;
    if (target < 0 || target >= questions.length) return;
    const qs = questions.slice();
    const tmp = qs[index];
    qs[index] = qs[target];
    qs[target] = tmp;
    onChange(qs);
  }

  return (
    <>
      <ol className="question-list" data-testid={testIdPrefix ?? 'quiz-question-list'}>
        {questions.map((q, i) => (
          <QuestionCard
            key={q.id}
            question={q}
            index={i}
            total={questions.length}
            showPreview={showPreview}
            onKindChange={(kind) => changeQuestionKind(i, kind)}
            onChange={(nq) => updateQuestion(i, nq)}
            onRemove={() => removeQuestion(i)}
            onMove={(dir) => moveQuestion(i, dir)}
            codeMaxChars={codeMaxChars}
          />
        ))}
      </ol>

      <div className="editor-addquestion">
        <span className="field-label">{t('host.editor.addQuestion')}</span>
        <div className="btn-row">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className="btn btn--ghost"
              onClick={() => addQuestion(k)}
              disabled={questionsFull}
              title={questionsFull ? t('host.editor.limits.questionsFull') : undefined}
              data-testid={`quiz-add-${k}`}
            >
              + {t(`kind.${k}`)}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
